import React, { useEffect } from 'react';
import { Control, Controller, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { AppForm, AppFormLabel, AppTextField } from '@/components/atoms';
import { useJapaneseNameKanaFields } from '@/hooks/useJapaneseNameKanaFields';

import type { TPatientCertificationForm } from './schema';

interface PatientInfoSectionProps {
  control: Control<TPatientCertificationForm>;
  watch: UseFormWatch<TPatientCertificationForm>;
  setValue: UseFormSetValue<TPatientCertificationForm>;
  isLoading?: boolean;
}

const getSurname = (value: string): string => {
  const normalized = String(value ?? '').trim().replace(/\u3000/g, ' ');
  if (!normalized) return '';
  return normalized.split(/\s+/)[0] ?? '';
};

export const PatientInfoSection: React.FC<PatientInfoSectionProps> = ({
  control,
  watch,
  setValue,
  isLoading = false,
}) => {
  const patientNameKanaValue = watch('name_kana') ?? '';
  const insuredNameKanaValue = watch('h_name_kana') ?? '';

  // BD 6.1 — Auto-kana realtime: 受診者氏名漢字 → 受診者氏名仮名 via IME composition events.
  const {
    nameInputProps: patientNameInputProps,
    kanaInputProps: patientNameKanaInputProps,
    syncLastKana: syncPatientLastKana,
  } = useJapaneseNameKanaFields({
    nameKanaValue: patientNameKanaValue,
    setNameValue: v => setValue('name', v, { shouldDirty: true, shouldValidate: false }),
    setKanaValue: v => setValue('name_kana', v, { shouldDirty: true, shouldValidate: false }),
    maxLength: 64,
  });

  // BD 6.1 — Auto-kana realtime: 被保険者名 → 被保険者氏名仮名.
  const {
    nameInputProps: insuredNameInputProps,
    kanaInputProps: insuredNameKanaInputProps,
    syncLastKana: syncInsuredLastKana,
  } = useJapaneseNameKanaFields({
    nameKanaValue: insuredNameKanaValue,
    setNameValue: v => setValue('h_name', v, { shouldDirty: true, shouldValidate: false }),
    setKanaValue: v => setValue('h_name_kana', v, { shouldDirty: true, shouldValidate: false }),
    maxLength: 64,
  });

  useEffect(() => {
    syncPatientLastKana(String(patientNameKanaValue ?? ''));
  }, [patientNameKanaValue, syncPatientLastKana]);

  useEffect(() => {
    syncInsuredLastKana(String(insuredNameKanaValue ?? ''));
  }, [insuredNameKanaValue, syncInsuredLastKana]);

  return (
    <>
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState: { error } }) => {
          const { onChange: kanaOnChange, onBlur: kanaOnBlur, ...kanaIME } =
            patientNameInputProps;
          return (
            <AppForm.Item
              label={<AppFormLabel label="受診者氏名漢字" />}
              validateStatus={error ? 'error' : undefined}
              help={error?.message}
            >
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <AppTextField
                  {...field}
                  status={error ? 'error' : undefined}
                  className="w-full sm:w-[197px]"
                  disabled={isLoading}
                  {...kanaIME}
                  onChange={e => {
                    field.onChange(e);
                    kanaOnChange(e);
                    setValue('h_name', getSurname(e.target.value), {
                      shouldDirty: true,
                      shouldValidate: false,
                    });
                  }}
                  onBlur={e => {
                    field.onBlur();
                    kanaOnBlur(e);
                    setValue('h_name', getSurname(e.target.value), {
                      shouldDirty: true,
                      shouldValidate: false,
                    });
                  }}
                />
                <div className="w-full text-left sm:w-[140px] sm:pl-4">受診者氏名仮名</div>
                <Controller
                  name="name_kana"
                  control={control}
                  render={({ field: kanaField }) => {
                    const {
                      onChange: kField_onChange,
                      onBlur: kField_onBlur,
                      ...kanaIMEK
                    } = patientNameKanaInputProps;
                    return (
                      <AppTextField
                        {...kanaField}
                        className="w-full sm:w-[197px]"
                        disabled={isLoading}
                        {...kanaIMEK}
                        onChange={e => {
                          kanaField.onChange(e);
                          kField_onChange(e);
                          setValue('h_name_kana', getSurname(e.target.value), {
                            shouldDirty: true,
                            shouldValidate: false,
                          });
                        }}
                        onBlur={e => {
                          kanaField.onBlur();
                          kField_onBlur(e);
                          setValue('h_name_kana', getSurname(e.target.value), {
                            shouldDirty: true,
                            shouldValidate: false,
                          });
                        }}
                      />
                    );
                  }}
                />
              </div>
            </AppForm.Item>
          );
        }}
      />

      <Controller
        name="h_name"
        control={control}
        render={({ field, fieldState: { error } }) => {
          const { onChange: hOnChange, onBlur: hOnBlur, ...hIME } =
            insuredNameInputProps;
          return (
            <AppForm.Item
              label={<AppFormLabel label="被保険者名" />}
              validateStatus={error ? 'error' : undefined}
              help={error?.message}
            >
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <AppTextField
                  {...field}
                  status={error ? 'error' : undefined}
                  className="w-full sm:w-[197px]"
                  disabled={isLoading}
                  {...hIME}
                  onChange={e => {
                    field.onChange(e);
                    hOnChange(e);
                  }}
                  onBlur={e => {
                    field.onBlur();
                    hOnBlur(e);
                  }}
                />
                <div className="w-full text-left sm:w-[140px] sm:pl-4">被保険者氏名仮名</div>
                <Controller
                  name="h_name_kana"
                  control={control}
                  render={({ field: kanaField }) => {
                    const {
                      onChange: hkOnChange,
                      onBlur: hkOnBlur,
                      ...hkIME
                    } = insuredNameKanaInputProps;
                    return (
                      <AppTextField
                        {...kanaField}
                        className="w-full sm:w-[197px]"
                        disabled={isLoading}
                        {...hkIME}
                        onChange={e => {
                          kanaField.onChange(e);
                          hkOnChange(e);
                        }}
                        onBlur={e => {
                          kanaField.onBlur();
                          hkOnBlur(e);
                        }}
                      />
                    );
                  }}
                />
              </div>
            </AppForm.Item>
          );
        }}
      />
    </>
  );
};
